import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { FadeInLeft, FadeInRight, StaggerContainer, StaggerItem } from './animations/AnimatedSection';
import CounterAnimation from './animations/CounterAnimation';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();

  const aboutStats = [
    { value: '150+', labelKey: 'about.stats.projects' },
    { value: '50+', labelKey: 'about.stats.clients' },
    { value: '10+', labelKey: 'about.stats.experience' },
    { value: '99%', labelKey: 'about.stats.satisfaction' },
  ];

  const expertise = [
    'Business Intelligence',
    'Machine Learning',
    'Data Engineering',
    'Cloud Analytics',
    'Predictive Modeling',
    'Data Visualization'
  ];

  return (
    <section id="about" className="py-32 bg-stone-50 relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-20 w-96 h-96 border border-gold/10 rounded-full"
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-64 h-64 border border-gold/10 rounded-full"
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, delay: 0.3, ease: 'easeOut' }}
        />
        {/* Animated dots */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gold/20 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
            }}
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <FadeInLeft>
            <motion.span
              className="text-xs font-medium text-gold tracking-[0.3em] uppercase mb-4 block"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {t('about.label')}
            </motion.span>
            
            <motion.h2
              className="font-serif text-4xl md:text-5xl text-navy font-light leading-tight mb-8 overflow-hidden"
            >
              <motion.span
                className="block"
                initial={{ y: 80 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {t('about.title1')}
              </motion.span>
              <motion.span
                className="block"
                initial={{ y: 80 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {t('about.title2')}
              </motion.span>
            </motion.h2>
            
            <motion.p
              className="text-navy/60 font-light text-lg leading-relaxed mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {t('about.description')}
            </motion.p>

            {/* Expertise Tags */}
            <StaggerContainer staggerDelay={0.08} className="flex flex-wrap gap-3 mb-10">
              {expertise.map((item, index) => (
                <StaggerItem key={index}>
                  <motion.span
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-gold/20 rounded-sm text-sm text-navy/70 font-light"
                    whileHover={{ 
                      scale: 1.05, 
                      borderColor: 'rgba(201, 169, 98, 0.5)',
                      backgroundColor: 'rgba(201, 169, 98, 0.1)'
                    }}
                  >
                    <CheckCircle className="w-3.5 h-3.5 text-gold" />
                    {item}
                  </motion.span>
                </StaggerItem>
              ))}
            </StaggerContainer>

            <motion.a
              href="#contact"
              className="inline-flex items-center gap-2 text-navy font-medium text-sm tracking-wide group"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              whileHover={{ x: 5 }}
            >
              {t('about.learnMore')}
              <motion.span
                className="w-8 h-px bg-navy group-hover:bg-gold"
                whileHover={{ width: 48 }}
                transition={{ duration: 0.3 }}
              />
            </motion.a>
          </FadeInLeft>

          {/* Right Stats */}
          <FadeInRight className="grid grid-cols-2 gap-6">
            {aboutStats.map((stat, index) => (
              <motion.div
                key={index}
                className="p-8 bg-white/70 backdrop-blur-md border border-gold/10 rounded-sm text-center"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ 
                  y: -5,
                  boxShadow: '0 20px 40px -15px rgba(201, 169, 98, 0.15)',
                  borderColor: 'rgba(201, 169, 98, 0.3)'
                }}
              >
                <div className="font-serif text-4xl md:text-5xl text-gold font-light mb-2">
                  <CounterAnimation 
                    value={stat.value} 
                    suffix={stat.value.includes('%') ? '%' : stat.value.includes('+') ? '+' : ''} 
                    duration={2}
                  />
                </div>
                <motion.div
                  className="text-navy/60 font-light text-sm"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  {t(stat.labelKey)}
                </motion.div>
              </motion.div>
            ))}
          </FadeInRight>
        </div>
      </div>
    </section>
  );
};

export default About;
